import type { TenantAppStatus } from "./types";

/**
 * Which `TenantAppStatus`es a dev host (localhost / a Vite dev server) may
 * exercise ahead of production — lets a DRAFT app be driven end-to-end via
 * `VITE_TENANT_HOST` before its provisioner run ever reaches LIVE.
 * DISABLED/DEPROVISIONING/DELETED are never included: those are terminal or
 * deliberately-off states, not "not live yet" ones.
 */
const DEV_USABLE_STATUSES: ReadonlySet<TenantAppStatus> = new Set(["DRAFT", "PROVISIONING", "FAILED"]);

/**
 * Is this resolved tenant usable right now? `found:false` and every status
 * other than LIVE are `false` on a real host; a dev host additionally allows
 * DRAFT/PROVISIONING/FAILED. Pure — the `isDevHost` flag is computed by the
 * caller (hostname / `import.meta.env.DEV`) so this stays testable without a
 * `window`.
 */
export function isTenantUsable(
  tenant: { found: boolean; status: TenantAppStatus | null },
  isDevHost: boolean,
): boolean {
  if (!tenant.found || !tenant.status) return false;
  if (tenant.status === "LIVE") return true;
  return isDevHost && DEV_USABLE_STATUSES.has(tenant.status);
}

/** `location.hostname` (or the Vite dev-server flag) is a local dev context. */
export function isDevHost(hostname: string, viteDev: boolean = false): boolean {
  return viteDev || hostname === "localhost" || hostname === "127.0.0.1";
}
